import { useState, useEffect } from "react";
import { useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Zap, Shield, TrendingUp, Users, Star, ChevronDown, MessageSquare, X, Loader2 } from "lucide-react";
import { AIChatBox } from "@/components/AIChatBox";
import { resolveReferralCode, setReferralCookie } from "@/lib/referralCookie";

const VERTICALS = [
  { name: "Fundable360", desc: "Business credit & funding solutions", color: "#84ab4a" },
  { name: "Share360", desc: "Revenue sharing & referral programs", color: "#1A6FD4" },
  { name: "Scale360", desc: "Business growth & scaling systems", color: "#E8620A" },
  { name: "Build360", desc: "Website & digital presence building", color: "#0FA8A8" },
  { name: "Protect360", desc: "Business insurance & protection", color: "#7B4FD4" },
];

const PLANS = [
  { key: "lite",     label: "Lite",     monthlyPrice: 199,  yearlyPrice: 1990,  setupFee: 199,  features: ["1 vertical", "Basic CRM", "Email support"] },
  { key: "standard", label: "Standard", monthlyPrice: 499,  yearlyPrice: 4990,  setupFee: 799,  features: ["3 verticals", "Full CRM", "Priority support", "Automation workflows"] },
  { key: "pro",      label: "Pro",      monthlyPrice: 899,  yearlyPrice: 8990,  setupFee: 1999, features: ["All verticals", "Full CRM + Wibiz", "Dedicated support", "Custom automations", "White-label options"] },
];

export default function LandingPage() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const urlRef = params.get("ref") ?? "";
  // URL ref wins when present; otherwise fall back to the 90-day wbz_ref
  // cookie captured on an earlier visit (bookmark, return visit, dropped
  // query param). Keeps the ref alive even when it's missing from this URL.
  const refCode = resolveReferralCode(urlRef);

  useEffect(() => {
    if (urlRef) setReferralCookie(urlRef);
  }, [urlRef]);

  const [submitted, setSubmitted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<"lite" | "standard" | "pro">("standard");
  const [showGrace, setShowGrace] = useState(false);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    businessName: "", verticalName: "", notes: "",
  });

  const repQuery = trpc.rep.getByCode.useQuery(
    { repCode: refCode },
    { enabled: !!refCode }
  );

  const createLead = trpc.lead.create.useMutation({
    onSuccess: () => {
      setSubmitted(true);
      toast.success("Application submitted! We'll be in touch within 24 hours.");
    },
    onError: (e) => toast.error(e.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.email.trim()) {
      toast.error("First name and email are required");
      return;
    }
    createLead.mutate({
      ...form,
      repCode: refCode || undefined,
      plan: selectedPlan,
      source: "referral_landing",
    });
  };

  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">("monthly");

  async function handlePlanCheckout(planKey: string) {
    // Preserve all URL params (ref, utm_*, v, source) in the checkout session.
    // ref falls back to the wbz_ref cookie when it's missing from this URL.
    const ref = refCode || undefined;
    const vertical = params.get("v") ?? "Scale360";
    const source = params.get("source") ?? params.get("utm_source") ?? "landing";
    setCheckoutLoading(planKey);
    try {
      const res = await fetch("/api/billing/scale360/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planKey, billingPeriod, ref, vertical, source }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        // Stripe not configured — fall back to contact form at top of page
        setSelectedPlan(planKey as any);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      window.location.href = data.url;
    } catch {
      setSelectedPlan(planKey as any);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setCheckoutLoading(null);
    }
  }

  const rep = repQuery.data;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-[#84ab4a]/15 border-2 border-[#84ab4a]/40 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#84ab4a]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">You're In!</h1>
          <p className="text-white/60 mb-6">
            Your application has been received. A BizConnect360 specialist will reach out within 24 hours to discuss your business goals.
          </p>
          {rep && (
            <div className="bg-[#0D1E35] border border-[#84ab4a]/20 rounded-xl p-4 text-sm text-white/50 mb-6">
              Referred by: <span className="text-[#84ab4a] font-semibold">{rep.firstName} {rep.lastName}</span>
            </div>
          )}
          <div className="flex flex-col gap-3">
            <div className="bg-[#0D1E35] border border-white/5 rounded-xl p-4 text-left">
              <div className="text-xs text-white/30 uppercase tracking-wider mb-2">What happens next</div>
              {[
                "Discovery call scheduled within 24 hours",
                "Comprehensive analysis of your business needs",
                "Custom plan and onboarding timeline",
                "Up and running within 7–14 days",
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-2.5 py-1.5">
                  <div className="w-5 h-5 rounded-full bg-[#84ab4a]/15 border border-[#84ab4a]/30 flex items-center justify-center text-[10px] font-bold text-[#84ab4a] flex-shrink-0">{i + 1}</div>
                  <span className="text-sm text-white/60">{step}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] text-white">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#84ab4a]/6 rounded-full blur-3xl" />
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-[#1A6FD4]/6 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-16 pb-12">
          {/* Header */}
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-[#84ab4a]/15 border border-[#84ab4a]/30 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#84ab4a]" />
              </div>
              <span className="font-bold text-lg">BizConnect360</span>
            </div>
            {rep && (
              <div className="flex items-center gap-2 bg-[#84ab4a]/10 border border-[#84ab4a]/25 rounded-full px-3 py-1.5 text-xs">
                <div className="w-5 h-5 rounded-full bg-[#84ab4a]/20 flex items-center justify-center text-[10px] font-bold text-[#84ab4a]">
                  {rep.firstName[0]}
                </div>
                <span className="text-white/60">Referred by</span>
                <span className="text-[#84ab4a] font-semibold">{rep.firstName} {rep.lastName}</span>
              </div>
            )}
          </div>

          {/* Hero content */}
          <div className="grid grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#84ab4a]/10 border border-[#84ab4a]/25 rounded-full px-3 py-1.5 text-xs text-[#84ab4a] font-semibold mb-5">
                <Star className="w-3 h-3" /> Business Growth Platform
              </div>
              <h1 className="text-4xl font-bold leading-tight mb-4">
                Connect. Scale.<br />
                <span className="text-[#84ab4a]">Dominate Your Market.</span>
              </h1>
              <p className="text-white/60 text-lg mb-6 leading-relaxed">
                BizConnect360 gives your business the CRM, automation, and growth tools used by top-performing companies — all in one unified platform.
              </p>
              <div className="flex flex-col gap-2.5 mb-8">
                {[
                  "Full CRM with pipeline management",
                  "Automated follow-up & appointment booking",
                  "Business funding, protection & growth verticals",
                  "Dedicated onboarding & support team",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-[#84ab4a] flex-shrink-0" />
                    <span className="text-white/70 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 text-sm text-white/30">
                <div className="flex items-center gap-1.5"><Users className="w-4 h-4" /> 500+ businesses</div>
                <div className="flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> 3x avg growth</div>
                <div className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> SOC2 compliant</div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-[#0D1E35] border border-white/8 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white mb-1">Get Started Today</h2>
              <p className="text-sm text-white/40 mb-5">
                {refCode ? `Referred by ${rep?.firstName ?? refCode} · ` : ""}Fill out the form and we'll contact you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3.5">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-white/50 mb-1.5 uppercase tracking-wide">First Name *</label>
                    <input
                      className="w-full bg-white/5 border border-white/10 text-white placeholder-white/20 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#84ab4a]/60 focus:ring-1 focus:ring-[#84ab4a]/30 transition-all"
                      placeholder="John"
                      value={form.firstName}
                      onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/50 mb-1.5 uppercase tracking-wide">Last Name</label>
                    <input
                      className="w-full bg-white/5 border border-white/10 text-white placeholder-white/20 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#84ab4a]/60 focus:ring-1 focus:ring-[#84ab4a]/30 transition-all"
                      placeholder="Smith"
                      value={form.lastName}
                      onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/50 mb-1.5 uppercase tracking-wide">Email *</label>
                  <input
                    type="email"
                    className="w-full bg-white/5 border border-white/10 text-white placeholder-white/20 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#84ab4a]/60 focus:ring-1 focus:ring-[#84ab4a]/30 transition-all"
                    placeholder="john@company.com"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-white/50 mb-1.5 uppercase tracking-wide">Phone</label>
                    <input
                      className="w-full bg-white/5 border border-white/10 text-white placeholder-white/20 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#84ab4a]/60 focus:ring-1 focus:ring-[#84ab4a]/30 transition-all"
                      placeholder="555-0100"
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-white/50 mb-1.5 uppercase tracking-wide">Business Name</label>
                    <input
                      className="w-full bg-white/5 border border-white/10 text-white placeholder-white/20 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#84ab4a]/60 focus:ring-1 focus:ring-[#84ab4a]/30 transition-all"
                      placeholder="Acme Corp"
                      value={form.businessName}
                      onChange={(e) => setForm((f) => ({ ...f, businessName: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/50 mb-1.5 uppercase tracking-wide">Area of Interest</label>
                  <select
                    className="w-full bg-[#0A1628] border border-white/10 text-white/80 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#84ab4a]/60 focus:ring-1 focus:ring-[#84ab4a]/30 transition-all"
                    value={form.verticalName}
                    onChange={(e) => setForm((f) => ({ ...f, verticalName: e.target.value }))}
                  >
                    <option value="">Select a service area</option>
                    {VERTICALS.map((v) => <option key={v.name} value={v.name}>{v.name} — {v.desc}</option>)}
                  </select>
                </div>

                {/* Plan selector */}
                <div>
                  <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wide">Plan Interest</label>
                  <div className="grid grid-cols-3 gap-2">
                    {PLANS.map((plan) => (
                      <button
                        key={plan.key}
                        type="button"
                        onClick={() => setSelectedPlan(plan.key as any)}
                        className={`p-2.5 rounded-xl border text-left transition-all ${selectedPlan === plan.key ? "border-[#84ab4a]/60 bg-[#84ab4a]/10" : "border-white/8 bg-white/3 hover:border-white/15"}`}
                      >
                        <div className={`text-xs font-bold mb-0.5 ${selectedPlan === plan.key ? "text-[#84ab4a]" : "text-white/70"}`}>{plan.label}</div>
                        <div className="text-[10px] text-white/30">Contact us</div>
                      </button>
                    ))}
                  </div>
                </div>

                {refCode && (
                  <div className="bg-[#84ab4a]/8 border border-[#84ab4a]/20 rounded-xl px-3 py-2 flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#84ab4a] flex-shrink-0" />
                    <span className="text-xs text-[#84ab4a]/80">Referral code applied: <strong className="font-mono">{refCode}</strong></span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={createLead.isPending}
                  className="w-full bg-[#84ab4a] hover:bg-[#6e9038] text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60 mt-1"
                >
                  {createLead.isPending ? "Submitting..." : (
                    <>Get Started <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
                <p className="text-[10px] text-white/20 text-center">No commitment required. We'll reach out within 24 hours.</p>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Verticals section */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold text-center mb-2">Our Business Verticals</h2>
        <p className="text-white/40 text-center text-sm mb-8">Comprehensive solutions across every dimension of your business</p>
        <div className="grid grid-cols-5 gap-4">
          {VERTICALS.map((v) => (
            <div key={v.name} className="bg-[#0D1E35] border border-white/5 rounded-2xl p-4 text-center hover:border-white/10 transition-all">
              <div className="w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: `${v.color}15`, border: `1px solid ${v.color}30` }}>
                <Zap className="w-5 h-5" style={{ color: v.color }} />
              </div>
              <div className="font-bold text-sm text-white mb-1">{v.name}</div>
              <div className="text-xs text-white/35 leading-relaxed">{v.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Plans section */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold text-center mb-2">Simple, Transparent Pricing</h2>
        <p className="text-white/40 text-center text-sm mb-6">Choose the plan that fits your business stage</p>

        {/* Billing period toggle */}
        <div className="flex items-center justify-center mb-8 gap-3">
          <span className={`text-sm font-semibold ${billingPeriod === "monthly" ? "text-white" : "text-white/40"}`}>Monthly</span>
          <button
            onClick={() => setBillingPeriod(p => p === "monthly" ? "yearly" : "monthly")}
            className={`relative w-12 h-6 rounded-full transition-colors ${billingPeriod === "yearly" ? "bg-[#84ab4a]" : "bg-white/15"}`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${billingPeriod === "yearly" ? "translate-x-7" : "translate-x-1"}`} />
          </button>
          <span className={`text-sm font-semibold ${billingPeriod === "yearly" ? "text-white" : "text-white/40"}`}>
            Annual
            {billingPeriod === "yearly" && <span className="ml-1.5 text-[10px] font-bold text-[#84ab4a] bg-[#84ab4a]/15 px-1.5 py-0.5 rounded-full">Save ~17%</span>}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-5">
          {PLANS.map((plan) => {
            const price = billingPeriod === "yearly" ? plan.yearlyPrice : plan.monthlyPrice;
            const priceLabel = billingPeriod === "yearly"
              ? `$${price.toLocaleString()}/yr`
              : `$${price}/mo`;
            const subLabel = billingPeriod === "monthly" && plan.setupFee > 0
              ? `+$${plan.setupFee} one-time setup`
              : billingPeriod === "yearly"
              ? `≈ $${Math.round(plan.yearlyPrice / 12)}/mo billed annually`
              : null;
            return (
              <div key={plan.key} className={`bg-[#0D1E35] border rounded-2xl p-6 relative ${plan.key === "standard" ? "border-[#84ab4a]/40" : "border-white/5"}`}>
                {plan.key === "standard" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#84ab4a] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">Most Popular</div>
                )}
                <div className="font-bold text-lg text-white mb-1">{plan.label}</div>
                <div className="mb-1">
                  <span className="text-2xl font-bold text-white">{priceLabel}</span>
                </div>
                {subLabel && <p className="text-[11px] text-white/35 mb-4">{subLabel}</p>}
                {!subLabel && <div className="mb-4" />}
                <div className="space-y-2 mb-6">
                  {plan.features.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-white/60">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#84ab4a] flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => handlePlanCheckout(plan.key)}
                  disabled={checkoutLoading !== null}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-70 ${plan.key === "standard" ? "bg-[#84ab4a] text-white hover:bg-[#6e9038]" : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"}`}
                >
                  {checkoutLoading === plan.key ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Get Started
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/5 py-8 text-center text-white/20 text-xs">
        <p>© 2025 BizConnect360 · Scale360 · WiBiz · All rights reserved</p>
        {refCode && <p className="mt-1 font-mono">Referral: {refCode}</p>}
      </div>

      {/* Grace floating chat widget */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {showGrace && (
          <div className="w-[360px] max-h-[520px] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#0A1628] to-[#0D1E35]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#84ab4a]/20 flex items-center justify-center">
                  <MessageSquare className="w-3.5 h-3.5 text-[#84ab4a]" />
                </div>
                <span className="text-white font-semibold text-sm">Grace — BizConnect360 Assistant</span>
              </div>
              <button onClick={() => setShowGrace(false)} className="text-white/40 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <AIChatBox
                api="/api/chat/public"
                chatId="landing-grace"
                initialMessages={[]}
                placeholder="Ask about BizConnect360 programs..."
                emptyStateMessage="Hi! I'm Grace, the BizConnect360 assistant. Ask me about our programs, how to join, or how associates earn."
                suggestedPrompts={[
                  "What is Fundable360?",
                  "How do I join BizConnect360?",
                  "What is Share360?",
                  "How do associates earn commissions?",
                ]}
                className="h-full"
              />
            </div>
          </div>
        )}
        <button
          onClick={() => setShowGrace(v => !v)}
          className="w-14 h-14 rounded-full bg-[#84ab4a] hover:bg-[#6e9038] text-white shadow-lg flex items-center justify-center transition-all"
          title="Ask Grace"
        >
          {showGrace ? <X className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}
